import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ChevronLeft, Save, LogOut, User, LayoutDashboard, 
  Loader2, Plus, X
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const specialtyOptions = [
  'Yoga', 'Pilates', 'Renforcement musculaire', 'HIIT', 'Cardio',
  'Mobilité', 'Stretching', 'Boxe', 'CrossFit', 'Running',
  'Natation', 'Cyclisme', 'Danse', 'Arts martiaux', 'Méditation'
];

export default function CoachProfilePage() {
  const navigate = useNavigate();
  const { user, logout, checkAuth } = useAuth();
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newSpecialty, setNewSpecialty] = useState('');
  
  const [profile, setProfile] = useState({
    bio: '',
    specialties: [],
    certifications: [],
    experience_years: 0,
    hourly_rate: 0,
    location: '',
  });

  const [userInfo, setUserInfo] = useState({
    name: '',
    phone: '',
    city: '',
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API_URL}/api/coaches/profile`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setProfile({
          bio: data.bio || '',
          specialties: data.specialties || [],
          certifications: data.certifications || [],
          experience_years: data.experience_years || 0,
          hourly_rate: data.hourly_rate || 0,
          location: data.location || '',
        });
        setUserInfo({
          name: data.name || user?.name || '',
          phone: user?.phone || '',
          city: user?.city || '',
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const profileResponse = await fetch(`${API_URL}/api/coaches/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(profile),
      });

      const userResponse = await fetch(`${API_URL}/api/users/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(userInfo),
      });

      if (profileResponse.ok && userResponse.ok) {
        toast.success(t('coachProfile.toastSaved'));
        await checkAuth();
      } else {
        toast.error(t('coachProfile.toastError'));
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(t('coachProfile.toastError'));
    } finally {
      setSaving(false);
    }
  };

  const addSpecialty = (specialty) => {
    if (specialty && !profile.specialties.includes(specialty)) {
      setProfile(prev => ({
        ...prev,
        specialties: [...prev.specialties, specialty]
      }));
    }
    setNewSpecialty('');
  };

  const removeSpecialty = (specialty) => {
    setProfile(prev => ({
      ...prev,
      specialties: prev.specialties.filter(s => s !== specialty)
    }));
  };

  const addCertification = () => {
    const cert = prompt(t('coachProfile.certPrompt'));
    if (cert && !profile.certifications.includes(cert)) {
      setProfile(prev => ({
        ...prev,
        certifications: [...prev.certifications, cert]
      }));
    }
  };

  const removeCertification = (cert) => {
    setProfile(prev => ({
      ...prev,
      certifications: prev.certifications.filter(c => c !== cert)
    }));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/coach/dashboard" className="text-muted-foreground hover:text-foreground">
                <ChevronLeft className="w-5 h-5" />
              </Link>
              <Link to="/">
                <img src="/logo.png" alt="Motivya" className="h-8 w-auto" />
              </Link>
            </div>
            
            <div className="flex items-center gap-3">
              <Link to="/coach/dashboard">
                <Button variant="ghost" size="sm">
                  <LayoutDashboard className="w-4 h-4" />
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-2 uppercase" data-testid="coach-profile-title">{t('coachProfile.title')}</h1>
        <p className="text-muted-foreground mb-8">{t('coachProfile.subtitle')}</p>

        <div className="space-y-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-6 mb-6">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-10 h-10 text-muted-foreground" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-bold">{user?.name}</h2>
                <p className="text-muted-foreground">{user?.email}</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('coachProfile.fullName')}</Label>
                <Input
                  id="name"
                  value={userInfo.name}
                  onChange={(e) => setUserInfo(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="profile-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">{t('coachProfile.phone')}</Label>
                <Input
                  id="phone"
                  value={userInfo.phone}
                  onChange={(e) => setUserInfo(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+32 xxx xx xx xx"
                  data-testid="profile-phone"
                />
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h3 className="font-bold uppercase">{t('coachProfile.presentation')}</h3>
            <div className="space-y-2">
              <Label htmlFor="bio">{t('coachProfile.bio')}</Label>
              <Textarea
                id="bio"
                value={profile.bio}
                onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                placeholder={t('coachProfile.bioPlaceholder')}
                rows={4}
                data-testid="profile-bio"
              />
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h3 className="font-bold uppercase">{t('coachProfile.specialties')}</h3>
            
            <div className="flex flex-wrap gap-2">
              {profile.specialties.map((spec) => (
                <span 
                  key={spec}
                  className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm flex items-center gap-2"
                >
                  {spec}
                  <button onClick={() => removeSpecialty(spec)} className="hover:text-destructive">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>

            <div className="flex gap-2 flex-wrap">
              {specialtyOptions
                .filter(s => !profile.specialties.includes(s))
                .slice(0, 6)
                .map((spec) => (
                <button
                  key={spec}
                  type="button"
                  onClick={() => addSpecialty(spec)}
                  className="px-3 py-1 border border-border rounded-full text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                >
                  + {spec}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h3 className="font-bold uppercase">{t('coachProfile.certifications')}</h3>
            
            <div className="flex flex-wrap gap-2">
              {profile.certifications.map((cert) => (
                <span 
                  key={cert}
                  className="px-3 py-1 bg-secondary/10 text-secondary rounded-full text-sm flex items-center gap-2"
                >
                  {cert}
                  <button onClick={() => removeCertification(cert)} className="hover:text-destructive">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <button
                type="button"
                onClick={addCertification}
                className="px-3 py-1 border border-dashed border-border rounded-full text-sm text-muted-foreground hover:border-secondary hover:text-secondary transition-colors"
              >
                <Plus className="w-3 h-3 inline mr-1" />
                {t('coachProfile.addCert')}
              </button>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h3 className="font-bold uppercase">{t('coachProfile.experienceAndRates')}</h3>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="experience">{t('coachProfile.yearsExperience')}</Label>
                <Input
                  id="experience"
                  type="number"
                  min={0}
                  value={profile.experience_years}
                  onChange={(e) => setProfile(prev => ({ ...prev, experience_years: parseInt(e.target.value) || 0 }))}
                  data-testid="profile-experience"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rate">{t('coachProfile.hourlyRate')}</Label>
                <Input
                  id="rate"
                  type="number"
                  min={0}
                  value={profile.hourly_rate}
                  onChange={(e) => setProfile(prev => ({ ...prev, hourly_rate: parseFloat(e.target.value) || 0 }))}
                  data-testid="profile-rate"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">{t('coachProfile.serviceArea')}</Label>
              <Input
                id="location"
                value={profile.location}
                onChange={(e) => setProfile(prev => ({ ...prev, location: e.target.value }))}
                placeholder={t('coachProfile.serviceAreaPlaceholder')}
                data-testid="profile-location"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              className="btn-primary"
              disabled={saving}
              data-testid="save-profile"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  {t('coachProfile.save')}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
